<template>
  <modal v-model="isOpen" width="560" height="auto" :scrollable="true">
    <div class="position-relative modal-outer-container">
      <div class="modal-header border-bottom">
        <h5 class="mb-0 ml-3 pl-2 audience-title">
          Who can see "<truncated-text
            :text="folder ? folder.name : ''"
            max="28ch"
          />"
        </h5>
        <button class="btn border-0" aria-label="Close" @click="close">
          <i class="fas fa-times fa-lg"></i>
        </button>
      </div>

      <div class="modal-body audience-body">
        <alert v-if="isLoading" class="my-3" />

        <div v-else>
          <p v-if="members.length" class="audience-total">
            {{ members.length }}
            {{ members.length === 1 ? "person" : "people" }} can see this folder
          </p>

          <div v-if="showFilter" class="input-group mb-3">
            <div class="input-group-prepend">
              <span class="input-group-text"
                ><i class="fas fa-search"></i
              ></span>
            </div>
            <input
              v-model="filter"
              type="text"
              class="form-control"
              placeholder="Filter by name"
            />
          </div>

          <div v-if="groups.length" class="audience-list">
            <div
              v-for="group in groups"
              :key="group.key"
              class="audience-group"
            >
              <button
                type="button"
                class="audience-group-title"
                :aria-expanded="!isCollapsed(group)"
                @click="toggle(group)"
              >
                <i
                  class="fas fa-fw"
                  :class="
                    isCollapsed(group) ? 'fa-caret-right' : 'fa-caret-down'
                  "
                ></i>
                {{ group.title }} · {{ group.members.length }}
                <span
                  v-if="group.note"
                  class="font-weight-normal text-muted-theme"
                >
                  · {{ group.note }}</span
                >
              </button>

              <div
                v-for="member in isCollapsed(group) ? [] : group.members"
                :key="member.id"
                class="
                  d-flex
                  align-items-center
                  py-2
                  border-bottom
                  audience-row
                "
              >
                <span class="audience-initials mr-3">{{
                  initials(member.name)
                }}</span>

                <div class="audience-person">
                  <div class="audience-name" :title="member.name">
                    {{ member.name }}
                  </div>
                  <!-- No `text-muted`: its fixed grey is nearly invisible on saturated themes. -->
                  <div
                    v-if="sharedByFor(member)"
                    class="small text-muted-theme audience-name"
                    :title="sharedByFor(member)"
                  >
                    {{ sharedByFor(member) }}
                  </div>
                </div>

                <div class="ml-3 text-right audience-meta">
                  <span class="badge badge-light">{{
                    roleLabel(member.role_id)
                  }}</span>
                  <div v-if="sinceFor(member)" class="small text-muted-theme">
                    {{ sinceFor(member) }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Administrators/managers are deliberately absent — their access is identical for every folder. -->
          <!-- A failed load is NOT an empty audience — a 403 must not read as "genuinely private". -->
          <alert v-if="loadFailed" class="my-3"
            >Could not load who can see this folder.</alert
          >
          <alert v-else-if="!members.length" class="my-3"
            >Nobody else can see this yet</alert
          >
          <alert v-else-if="!groups.length" class="my-3"
            >No one matching "{{ filter }}"</alert
          >
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-transparent mr-2" @click="close">Close</button>
        <button
          v-if="canShare"
          class="btn btn-theme"
          @click="$emit('manage-access', folder)"
        >
          Manage access
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import helpers from "@/utils/helpers";
import { mapActions } from "vuex";
import { folderPermissions, roleLabel } from "./useDocumentsArea";

export default {
  name: "AudienceDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    folder: { type: Object, default: null },
  },
  emits: ["update:modelValue", "manage-access"],
  data() {
    return {
      members: [],
      filter: "",
      collapsed: {},
      isLoading: false,
      loadFailed: false,
      loadToken: 0,
    };
  },
  computed: {
    /**
     * Sections replace a per-row reason sentence: the reason is the same for everyone in a
     * group, and repeating it meant a second full name on every line.
     */
    groups() {
      const buckets = [
        { key: "owner", title: "Owner", match: (m) => m.reason === "owner" },
        {
          key: "shared",
          title: "Shared directly",
          match: (m) => m.reason === "shared",
        },
        {
          key: "inherited",
          title: "Shared on a folder above",
          note: "reaches it from there",
          match: (m) => m.reason === "inherited",
        },
        {
          key: "care_team",
          title: "Care team",
          note: "reaches it automatically",
          match: (m) => this.isAutomatic(m),
        },
      ];

      const needle = this.filter.trim().toLowerCase();
      const visible = needle
        ? this.members.filter((m) =>
            (m.name || "").toLowerCase().includes(needle)
          )
        : this.members;

      return buckets
        .map((bucket) => ({
          ...bucket,
          members: visible.filter(bucket.match),
        }))
        .filter((bucket) => bucket.members.length);
    },

    /** Only worth the space once the list is long enough to actually scroll. */
    showFilter() {
      return this.members.length > 10;
    },

    canShare() {
      return !!this.folder && folderPermissions(this, this.folder).share;
    },

    isOpen: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
  },
  watch: {
    modelValue(open) {
      if (open && this.folder) {
        this.load();
      }
    },
  },
  methods: {
    ...mapActions({ getAudience: "folders/getAudience" }),

    load() {
      const token = ++this.loadToken;
      this.filter = "";
      this.collapsed = {};
      this.isLoading = true;
      this.loadFailed = false;

      this.getAudience(this.folder.id)
        .then((data) => {
          if (token !== this.loadToken) {
            return;
          }
          this.members = data.members || [];
        })
        .catch(() => {
          if (token !== this.loadToken) {
            return;
          }
          this.members = [];
          this.loadFailed = true;
        })
        .finally(() => {
          if (token !== this.loadToken) {
            return;
          }
          this.isLoading = false;
        });
    },

    close() {
      this.isOpen = false;
    },

    /** Names arrive as "last, first", so the initials read in that order too. */
    initials(name) {
      if (!name) {
        return "?";
      }

      return name
        .split(",")
        .map((part) => part.trim().charAt(0))
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },

    roleLabel(roleId) {
      return roleLabel(this, roleId);
    },

    isAutomatic(member) {
      return (
        member.reason === "care_team" ||
        member.reason === "care_team_of_recipient"
      );
    },

    /**
     * The care team is the longest section and the least surprising — it is derived, not a
     * decision anyone made — so it starts closed once it would bury the explicit shares.
     * Filtering always expands everything.
     */
    isCollapsed(group) {
      if (this.filter.trim()) {
        return false;
      }

      return (
        this.collapsed[group.key] ??
        (group.key === "care_team" && group.members.length > 8)
      );
    },

    toggle(group) {
      this.collapsed = {
        ...this.collapsed,
        [group.key]: !this.isCollapsed(group),
      };
    },

    /** Only what the section heading does not already say. The date lives in its own column. */
    sharedByFor(member) {
      if (member.reason === "care_team_of_recipient" && member.reason_detail) {
        return `via ${member.reason_detail}`;
      }

      if (member.reason === "inherited" && member.reason_detail) {
        return `shared on "${member.reason_detail}"`;
      }

      if (member.reason !== "shared") {
        return "";
      }

      const currentUserId =
        this.$store.state.auth.user && this.$store.state.auth.user.id;

      if (
        member.shared_by &&
        currentUserId &&
        Number(member.shared_by) === Number(currentUserId)
      ) {
        return "by you";
      }

      return member.shared_by_name ? `by ${member.shared_by_name}` : "";
    },

    /** Short and fixed-width, so it gets its own column rather than being truncated away. */
    sinceFor(member) {
      return member.since ? helpers.prettyDate(member.since) : "";
    },
  },
};
</script>

<style scoped>
.audience-row:last-of-type {
  border-bottom: 0 !important;
}

/* `--theme-color` with `--btn-text-color` (a themed pair), not `--secondary-color`, which lacks
   a matching contrast guarantee and left initials barely legible on some themes. */
.audience-initials {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--theme-color, #e9ecef);
  color: var(--btn-text-color, #000000);
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}

/* Full body size, not uppercase and not dimmed: the reason someone can see a folder is the
   point of this dialog, and this client's team reads small type badly. */
.audience-group-title {
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 700;
  color: var(--main-text-color);
  padding-bottom: 0.35rem;
}

.audience-title {
  min-width: 0;
  overflow-wrap: anywhere;
  font-size: 1.15rem;
  font-weight: 700;
}

.audience-total {
  font-weight: 600;
  margin-bottom: 0.75rem;
}

/* The list scrolls inside the body so the title and buttons stay put. */
.audience-body {
  /* A tint behind the list, so the white rows read as a surface rather than blending into it —
     the same treatment the move dialog uses. */
  background: rgba(0, 0, 0, 0.025);
  max-height: 55vh;
  overflow-y: auto;
}

.audience-list {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: 0.75rem 0.9rem;
}

.audience-group + .audience-group {
  margin-top: 1.5rem;
}

/* `min-width: 0` is what actually lets the name shrink — a flex item defaults to
   `min-width: auto`, which refuses to go below its content and forced the old layout to wrap
   one word per line. */
.audience-person {
  flex: 1 1 auto;
  min-width: 0;
}

/* Names carry credentials and licence numbers, so one line plus the full value on hover. */
.audience-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.audience-meta {
  flex-shrink: 0;
  white-space: nowrap;
}
</style>
